import React from 'react';
import CTASection from '../components/CTASection';
import '../styles/Privacy.css';

const Privacy = () => {
  return (
    <div>
      <div className="privacy-page section-marg">
        <div className="container">
          <h1>Privacy Policy</h1>
          <p className="updated mb-16 mt-16"><b>Last updated:</b> January 2, 2026</p>
          
          <div className="mb-24">
            <h3 className="page-title mb-8">
              1. Introduction
            </h3>
            <p className="page-intro">
              Fiktion ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our creative writing and story development platform ("the Service").
            </p>
          </div>

          <div className="mb-24">
            <h3 className="page-title mb-8">
              2. Information We Collect
            </h3>
            <p className="page-intro mb-16">
              <b>2.1 Account Information</b><br />
              To create and maintain your account, we require your email address for account creation and authentication. We use Firebase Authentication, which may collect your email address, Google account information if you choose to sign in with Google, and authentication tokens and session data to maintain your secure access to the Service.
            </p>
            <p className="page-intro mb-16">
              <b>2.2 Content Data</b><br />
              We store all content that you create and upload through the Service. This includes your written scripts with dialogue, action lines, and screenplay formatting; character data including names, descriptions, traits, goals, conflicts, personality, arcs, and related information; location data including names, descriptions, and details; story elements such as beats, sequences, story structure, and outline information; ideas and notes including your creative ideas, notes, and project-related content; and project data including project names, settings, preferences, and metadata.
            </p>
            <p className="page-intro mb-16">
              <b>2.3 Usage Data</b><br />
              We may collect information about how you use the Service, including feature usage and interaction patterns, device information such as browser type and operating system, IP address and general location data, and timestamps of activities. This information helps us understand how the Service is being used and enables us to improve the user experience.
            </p>
            <p className="page-intro">
              <b>2.4 Technical Data</b><br />
              We collect technical information including cookies and similar tracking technologies, log files and error reports, and performance and diagnostic data. This technical data helps us maintain the reliability and security of the Service.
            </p>
          </div>

          <div className="mb-24">
            <h3 className="page-title mb-8">
              3. How We Use Your Information
            </h3>
            <p className="page-intro mb-16">
              <b>3.1 Service Provision</b><br />
              We use your information to provide, maintain, and improve the Service, authenticate your identity and secure your account, store and sync your content across devices, enable features like autosave, version history, and collaboration, and process your requests and transactions.
            </p>
            <p className="page-intro mb-16">
              <b>3.2 Communication</b><br />
              We may use your email address to send authentication links and security notifications, provide customer support, send important service updates and announcements, and respond to your inquiries. We will only send you non-essential communications if you have consented to receive them.
            </p>
            <p className="page-intro mb-16">
              <b>3.3 Security and Legal Compliance</b><br />
              We use your information to detect and prevent fraud, abuse, and security threats, comply with legal obligations, enforce our Terms of Service, and protect our rights and the rights of our users. This helps us maintain a secure and trustworthy environment for all users.
            </p>
            <p className="page-intro">
              <b>3.4 Legal Basis for Processing</b><br />
              We process your personal data on the following legal bases:
              <ul>
                <li>Performance of a contract (to provide the Service to you)</li>
                <li>Compliance with legal obligations</li>
                <li>Legitimate interests (such as improving the Service and ensuring security)</li>
                <li>Consent, where required by law</li>
              </ul>
            </p>
          </div>

          <div className="mb-24">
            <h3 className="page-title mb-8">
              4. Data Storage and Security
            </h3>
            <p className="page-intro mb-16">
              <b>4.1 Data Storage</b><br />
              Your data is stored on secure cloud servers provided by Firebase and Google Cloud Platform. All data is stored in encrypted form using industry-standard encryption algorithms, and we implement both encryption in transit using SSL/TLS protocols and encryption at rest to protect your information.
            </p>
            <p className="page-intro mb-16">
              <b>4.2 Security Measures</b><br />
              We employ various security measures to protect your information. Sensitive content including scripts, characters, locations, and ideas is encrypted before storage. User authentication is required to access your data, and data is isolated per user account. We regularly update our security practices and infrastructure, and we monitor for security threats and unauthorized access to ensure the ongoing protection of your information.
            </p>
            <p className="page-intro">
              <b>4.3 Data Retention</b><br />
              We retain your data for as long as your account is active. You may delete your account and request data deletion at any time. Some data may be retained for legal or legitimate business purposes as required by law or to resolve disputes. Deleted data may persist in backups for a limited time before permanent deletion as part of our disaster recovery procedures.
            </p>
          </div>

          <div className="mb-24">
            <h3 className="page-title mb-8">
              5. Data Sharing and Disclosure
            </h3>
            <p className="page-intro mb-16">
              <b>5.1 We Do Not Sell Your Data</b><br />
              We do not sell, rent, or trade your personal information or content to third parties. Your creative work is your own and we do not monetize it or share it with advertisers or other third parties for their commercial purposes.
            </p>
            <p className="page-intro mb-16">
              <b>5.2 Service Providers</b><br />
              We may share your information, but not your creative work content, with trusted third-party service providers who assist us in operating the Service. These include Firebase and Google Cloud Platform for hosting and data storage services, Mailgun for email delivery services such as authentication emails, and payment processors if you use paid features, though payment information is handled directly by the processors and not shared with us. These service providers are contractually obligated to protect your information and use it only for the purposes we specify.
            </p>
            <p className="page-intro mb-16">
              <b>5.3 Legal Requirements</b><br />
              We may disclose your information if required by law or in response to valid legal requests such as subpoenas or court orders, government investigations, to protect our rights, property, or safety, or to prevent fraud or abuse. We will only disclose the minimum amount of information necessary to comply with such legal obligations.
            </p>
            <p className="page-intro">
              <b>5.4 Business Transfers</b><br />
              In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity, subject to the same privacy protections outlined in this Privacy Policy. We will notify you of any such transfer and provide you with an opportunity to delete your account if you do not wish to continue with the new entity.
            </p>
          </div>

          <div className="mb-24">
            <h3 className="page-title mb-8">
              6. Your Rights and Choices
            </h3>
            <p className="page-intro mb-16">
              <b>6.1 Access and Correction</b><br />
              You can access and update your account information through the Service at any time. You can also view and edit your content at any time, giving you full control over the information you have shared with us.
            </p>
            <p className="page-intro mb-16">
              <b>6.2 Data Export</b><br />
              You can export your content in various formats including PDF and XML through the Service's export functionality. We recommend regular exports to maintain local backups of your work, as outlined in our Terms of Service.
            </p>
            <p className="page-intro mb-16">
              <b>6.3 Account Deletion</b><br />
              You can delete your account at any time through your account settings. Upon deletion, we will delete your personal information and content, subject to legal retention requirements. Some information may remain in backups for a limited period before permanent deletion.
            </p>
            <p className="page-intro">
              <b>6.4 Email Preferences</b><br />
              You can opt out of non-essential emails, though authentication emails are required for account access and cannot be disabled. You can contact us to update your communication preferences at any time.
            </p>
          </div>

          <div className="mb-24">
            <h3 className="page-title mb-8">
              7. Cookies and Tracking Technologies
            </h3>
            <p className="page-intro mb-16">
              <b>7.1 Cookies</b><br />
              We use cookies and similar technologies to maintain your session and authentication state, remember your preferences, and analyze Service usage and performance. These technologies help us provide you with a personalized and secure experience.
            </p>
            <p className="page-intro">
              <b>7.2 Cookie Controls</b><br />
              Most browsers allow you to control cookies through their settings. However, disabling cookies may affect Service functionality, particularly features that require maintaining your session state or remembering your preferences.
            </p>
          </div>

          <div className="mb-24">
            <h3 className="page-title mb-8">
              8. Children's Privacy
            </h3>
            <p className="page-intro">
              The Service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately so we can take appropriate action to remove such information.
            </p>
          </div>

          <div className="mb-24">
            <h3 className="page-title mb-8">
              9. International Data Transfers
            </h3>
            <p className="page-intro">
              Your information may be transferred to and processed in countries other than your country of residence. These countries may have different data protection laws than your home country. We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy and applicable data protection regulations.
            </p>
          </div>

          <div className="mb-24">
            <h3 className="page-title mb-8">
              10. Data Breach Notification
            </h3>
            <p className="page-intro">
              In the event of a data breach that may affect your personal information, we will investigate the breach promptly, notify affected users as required by applicable law, and take appropriate remedial actions to prevent further unauthorized access and to mitigate any potential harm.
            </p>
          </div>

          <div className="mb-24">
            <h3 className="page-title mb-8">
              11. Third-Party Links and Services
            </h3>
            <p className="page-intro">
              The Service may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties. We encourage you to review their privacy policies before providing any personal information to them, as their practices may differ from ours.
            </p>
          </div>

          <div className="mb-24">
            <h3 className="page-title mb-8">
              12. Changes to This Privacy Policy
            </h3>
            <p className="page-intro">
              We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of material changes by posting the updated policy on our website, sending an email notification if you have an account, or displaying a notice in the Service. Your continued use of the Service after such changes constitutes acceptance of the updated Privacy Policy.
            </p>
          </div>

          <div className="mb-24">
            <h3 className="page-title mb-8">
              13. Your Consent
            </h3>
            <p className="page-intro">
              By using the Service, you consent to the collection and use of your information as described in this Privacy Policy, the storage of your data on cloud servers operated by our service providers, and the processing of your data as necessary to provide the Service to you.
            </p>
          </div>

          <div className="mb-24">
            <h3 className="page-title mb-8">
              14. Your Rights Under GDPR (European Users)
            </h3>
            <p className="page-intro">
              If you are located in the European Economic Area (EEA), you have certain rights under the General Data Protection Regulation (GDPR). These include the right to access your personal data and request a copy of it, the right to rectification and correction of inaccurate data, the right to erasure and deletion of your data, the right to restrict processing of your data, the right to data portability and transfer of your data, and the right to object to certain types of data processing. To exercise these rights, please contact us using the contact information provided below.
            </p>
          </div>
          <div className="mb-24">
            <h3 className="page-title mb-8">
              Data Protection Contact
            </h3>
            <p className="page-intro">
            If you have any questions about this Privacy Policy or wish to exercise your rights under applicable data protection laws, you may contact us at:
            <br />
            <a href="mailto:help@fiktion.io">help@fiktion.io</a>
            </p>
          </div>
        </div>
      </div>

      <div className="divider"></div>

      <CTASection />
    </div>
  );
};

export default Privacy;

